import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { WholesaleStatus, type WholesaleOrderSummary } from '@tucupet/shared';
import { PageHeader, Table, Select, Field, Pager } from '../components/ui';
import { api } from '../lib/api';

const STATUS_OPTIONS: { value: '' | WholesaleStatus; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: WholesaleStatus.SUBMITTED, label: 'En revisión' },
  { value: WholesaleStatus.APPROVED, label: 'Aprobada' },
  { value: WholesaleStatus.REJECTED, label: 'Rechazada' },
  { value: WholesaleStatus.PAID, label: 'Pagada' },
  { value: WholesaleStatus.DELIVERED, label: 'Entregada' },
  { value: WholesaleStatus.DRAFT, label: 'Borrador' },
  { value: WholesaleStatus.CANCELLED, label: 'Cancelada' },
];

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  draft: { bg: 'var(--surface-2)', fg: 'var(--text-3)' },
  submitted: { bg: 'color-mix(in oklab, oklch(0.78 0.16 70) 14%, transparent)', fg: 'oklch(0.78 0.16 70)' },
  approved: { bg: 'color-mix(in oklab, var(--accent) 14%, transparent)', fg: 'var(--accent)' },
  rejected: { bg: 'color-mix(in oklab, var(--danger) 14%, transparent)', fg: 'var(--danger)' },
  paid: { bg: 'color-mix(in oklab, var(--accent) 14%, transparent)', fg: 'var(--accent)' },
  delivered: { bg: 'color-mix(in oklab, var(--accent) 14%, transparent)', fg: 'var(--accent)' },
  cancelled: { bg: 'color-mix(in oklab, var(--danger) 14%, transparent)', fg: 'var(--danger)' },
};

export default function WholesalePage() {
  const [sp, setSp] = useSearchParams();
  const status = (sp.get('status') ?? '') as '' | WholesaleStatus;
  const search = sp.get('search') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const { data, isLoading } = useQuery<{ items: WholesaleOrderSummary[]; total: number; page: number; limit: number }>({
    queryKey: ['admin', 'wholesale', status, search, page],
    queryFn: () => api('/api/admin/wholesale/orders', { query: { status: status || undefined, search: search || undefined, page } }),
  });

  const setFilter = (next: Partial<{ status: string; search: string; page: string }>) => {
    const merged: Record<string, string> = {};
    const cur = { status, search, page: String(page) };
    for (const k of Object.keys(cur) as (keyof typeof cur)[]) {
      const v = next[k] !== undefined ? next[k] : cur[k];
      if (v && !(k === 'page' && v === '1')) merged[k] = v as string;
    }
    setSp(merged);
  };

  return (
    <div>
      <PageHeader kicker="MAYORISTA" title={`Pedidos mayoristas · ${data?.total ?? 0}`} sub="Revisa documentos legales y aprueba o rechaza pedidos por contenedor." />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 220 }}>
          <Select<'' | WholesaleStatus>
            label="Estado"
            value={status}
            onChange={(v) => setFilter({ status: v, page: '1' })}
            options={STATUS_OPTIONS}
          />
        </div>
        <div style={{ minWidth: 260, flex: 1 }}>
          <Field label="Buscar" value={search} onChange={(v) => setFilter({ search: v, page: '1' })} placeholder="Código, razón social, CIF, email" />
        </div>
      </div>

      <Table<WholesaleOrderSummary>
        rows={data?.items ?? []}
        empty={isLoading ? 'Cargando…' : 'Sin pedidos mayoristas'}
        columns={[
          {
            key: 'code',
            label: 'Código',
            render: (r) => (
              <Link to={`/wholesale/${r.code}`} className="mono" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                {r.code}
              </Link>
            ),
          },
          { key: 'titular', label: 'Titular', render: (r) => r.customer?.businessName ?? <span style={{ color: 'var(--text-3)' }}>—</span> },
          { key: 'tipo', label: 'Tipo', render: (r) => <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{(r.customer?.type ?? '').toUpperCase()}</span> },
          { key: 'liters', label: 'Litros', render: (r) => <span className="mono">{r.totalLiters.toLocaleString('es-MX')} L</span> },
          { key: 'total', label: 'Total', render: (r) => <span className="mono" style={{ fontWeight: 600 }}>${r.totalAmount.toFixed(2)}</span> },
          {
            key: 'foreign',
            label: 'Pagador',
            render: (r) =>
              r.foreignPayer ? (
                <span className="mono" style={{ fontSize: 10, color: 'oklch(0.78 0.16 70)', letterSpacing: '0.06em' }}>{r.foreignPayer.country.toUpperCase()}</span>
              ) : (
                <span style={{ color: 'var(--text-3)' }}>Titular</span>
              ),
          },
          {
            key: 'status',
            label: 'Estado',
            render: (r) => {
              const c = STATUS_COLORS[r.status] ?? STATUS_COLORS.draft!;
              return (
                <span
                  className="mono"
                  style={{ fontSize: 11, padding: '3px 8px', borderRadius: 999, background: c.bg, color: c.fg, letterSpacing: '0.08em' }}
                >
                  {r.status.toUpperCase()}
                </span>
              );
            },
          },
          { key: 'date', label: 'Fecha', render: (r) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{new Date(r.createdAt).toLocaleString('es-MX')}</span> },
        ]}
      />

      {data && <Pager total={data.total} page={data.page} limit={data.limit} onPage={(p) => setFilter({ page: String(p) })} />}
    </div>
  );
}
